import { z } from 'zod'
import { fetchWithAuth, supabase } from './api/client'
import { getCachedWallet, loadWallet } from './wallet-utils'
import { payForListing } from './x402/client'

const API_URL = import.meta.env.VITE_API_URL!

/**
 * Creates an artifact tool definition for AI model tool calling on the frontend.
 * This version calls the backend API endpoints instead of directly accessing R2.
 */
export function createArtifactTool(sessionId: string) {
  return {
    create_artifact: {
      description:
        'Create an HTML artifact that will be displayed to the user. Use this to create interactive documents, visualizations, dashboards, games, or any HTML content. The artifact will be saved and can be opened in a window. Include complete HTML with <!DOCTYPE html>, <html>, <head>, and <body> tags. You can use inline CSS and JavaScript, or load libraries from CDNs.',
      inputSchema: z.object({
        name: z
          .string()
          .describe('Filename with .html extension (e.g., "dashboard.html")'),
        content: z
          .string()
          .describe(
            'Complete HTML content including DOCTYPE, html, head, and body tags'
          ),
        description: z
          .string()
          .optional()
          .describe('Brief description of what this artifact does'),
        language: z
          .string()
          .optional()
          .describe('Programming language - use "html" for now'),
      }),
      execute: async ({
        name,
        content,
        description,
        language = 'html',
      }: {
        name: string
        content: string
        description?: string
        language?: string
      }) => {
        try {
          if (!name.endsWith('.html')) {
            throw new Error('Filename must end with .html')
          }

          if (content.length > 5 * 1024 * 1024) {
            throw new Error('Content too large (max 5MB)')
          }

          const response = await fetchWithAuth(`${API_URL}/api/artifacts/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name,
              content,
              description,
              language,
              sessionId,
            }),
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to create artifact')
          }

          const data = await response.json()

          return {
            id: data.id,
            name: data.name,
            url: `/artifacts/${data.id}`,
          }
        } catch (error) {
          console.error('Error in createArtifactTool:', error)
          throw error
        }
      },
    },
    update_artifact: {
      description:
        'Update an existing HTML artifact. Use this when the user asks you to modify, edit, or update an existing artifact. You can update the name, content, description, or any combination of these fields. Only provide the fields you want to update. IMPORTANT: The "id" parameter must be the artifact ID from create_artifact or list_artifacts - this is a hex string like "a1b2c3d4e5f6...".',
      inputSchema: z.object({
        id: z
          .string()
          .describe(
            'The artifact ID (hex string like "a1b2c3d4e5f6...") from the "id" field in list_artifacts or create_artifact response.'
          ),
        name: z
          .string()
          .optional()
          .describe('New filename with .html extension (optional)'),
        content: z
          .string()
          .optional()
          .describe('New complete HTML content (optional)'),
        description: z
          .string()
          .optional()
          .describe('New description (optional)'),
        language: z
          .string()
          .optional()
          .describe('New programming language (optional)'),
      }),
      execute: async ({
        id,
        name,
        content,
        description,
        language,
      }: {
        id: string
        name?: string
        content?: string
        description?: string
        language?: string
      }) => {
        try {
          // Validate ID format (should be a hex string)
          if (!id || !/^[a-f0-9]+$/i.test(id)) {
            throw new Error(
              `Invalid artifact ID format: "${id}". Expected a hex string (e.g., "a1b2c3d4e5f6...").`
            )
          }

          if (name && !name.endsWith('.html')) {
            throw new Error('Filename must end with .html')
          }

          if (content && content.length > 5 * 1024 * 1024) {
            throw new Error('Content too large (max 5MB)')
          }

          const response = await fetchWithAuth(
            `${API_URL}/api/artifacts/${id}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name,
                content,
                description,
                language,
              }),
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to update artifact')
          }

          const data = await response.json()

          return {
            id: data.id,
            name: data.name,
            url: `/artifacts/${id}`,
          }
        } catch (error) {
          console.error('Error in update_artifact tool:', error)
          throw error
        }
      },
    },
    list_artifacts: {
      description:
        'List all artifacts created by the user. Use this to see what artifacts are available to update or reference. Returns a list of artifacts with their IDs, names, descriptions, and creation dates.',
      inputSchema: z.object({}),
      execute: async () => {
        try {
          const response = await fetchWithAuth(`${API_URL}/api/artifacts/list`, {
            method: 'GET',
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to list artifacts')
          }

          const data = await response.json()

          return { artifacts: data.artifacts || [] }
        } catch (error) {
          console.error('Error in list_artifacts tool:', error)
          throw error
        }
      },
    },
    read_artifact: {
      description:
        'Read the full contents of an existing artifact. Use this when you need to see the actual content of an artifact to understand it, modify it, or reference it. Returns the complete artifact data including its HTML content.',
      inputSchema: z.object({
        id: z
          .string()
          .describe(
            'The artifact ID (hex string like "a1b2c3d4e5f6...") from the "id" field in list_artifacts or create_artifact response.'
          ),
      }),
      execute: async ({ id }: { id: string }) => {
        try {
          // Validate ID format (should be a hex string)
          if (!id || !/^[a-f0-9]+$/i.test(id)) {
            throw new Error(
              `Invalid artifact ID format: "${id}". Expected a hex string (e.g., "a1b2c3d4e5f6...").`
            )
          }

          const response = await fetchWithAuth(`${API_URL}/api/artifacts/${id}`, {
            method: 'GET',
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(
              errorData.error || `Artifact not found with ID "${id}".`
            )
          }

          const data = await response.json()

          return {
            id: data.artifact.id,
            name: data.artifact.name,
            content: data.artifact.content,
            description: data.artifact.description,
            language: data.artifact.language,
            type: data.artifact.type,
            lastModified: data.artifact.lastModified,
          }
        } catch (error) {
          console.error('Error in read_artifact tool:', error)
          throw error
        }
      },
    },
  }
}

/**
 * Creates a theme tool definition for AI model tool calling on the frontend.
 * This version calls the backend API endpoints instead of directly accessing R2.
 */
export function createThemeTool(sessionId: string) {
  return {
    create_theme: {
      description:
        'Create a theme from a natural-language description. Generate ALL colors based on the theme description - do not use any hardcoded or default color values. REQUIRED FORMAT: All themes MUST have both "light" and "dark" modes with a "colors" object in each. Use these EXACT color property names: primaryBg, secondaryBg, terminalBg, headerBg, primaryBorder, secondaryBorder, headerBorder, terminalBorder, primaryText, secondaryText, tertiaryText, darkText, lightText, textUser, headerText, secondaryHeaderText, visualAccent1, visualAccent2, visualAccent3, visualAccent4, primaryAccent, hoverAccent, successBg, successBorder, errorBg, errorBorder, assistantBg, systemMsgBg, userBg, userMsgBg, assistantMsgBg, assistantMsgGlow, assistantMsgAccent, userMsgBgAlpha, userMsgGlow, userMsgAccent, scrollbarThumb, scrollbarTrack, gridOverlay, white, gridLine, windowBg, windowBgAlpha, windowBorder, windowBorderActive, windowHeaderBg, windowHeaderBorder, windowControlBg, windowControlBgHover, navBg, navBorder, navTabBgActive, navTabBgHover, mobileNavBg, mobileNavBorder, mobileNavIconHover, mobileNavIconActive, folderSelectedBg, folderSelectedText, emptyStateText, webIndicatorOnBg, webIndicatorOnBorder, webIndicatorOffBg, webIndicatorOffBorder, orbListening, orbSpeaking, orbProcessing, orbListeningGlow, orbSpeakingGlow, orbProcessingGlow, orbDefaultGlow. CRITICAL STYLING RULES: 1) Generate ALL colors to match the theme description - create a cohesive, tasteful, harmonious color palette. 2) Light mode contrast: message backgrounds must use opacity 0.15-0.25 for visibility. 3) assistantMsgAccent and userMsgAccent must be distinct, vibrant border colors derived from the theme - use saturated colors in light mode, lighter colors in dark mode. 4) visualAccent1, visualAccent2, visualAccent3, visualAccent4 must be unique accent colors that fit the theme palette. 5) webIndicatorOnBg/Border should use theme accent colors with proper opacity for "on" state, webIndicatorOffBg/Border should be subtle neutral colors. 6) scrollbarThumb/Track should be subtle colors that complement the theme - lighter for light mode, darker for dark mode. 7) secondaryText must have strong contrast for navigation readability. 8) Use assistantMsgBg (not assistantBg), userMsgBgAlpha (not userBg) for message bubbles. 9) folderSelectedBg/Text must have high contrast (WCAG AA 4.5:1+) - use vibrant background with white text in both modes. 10) emptyStateText must be readable on primary background - use medium gray for light mode (#6B7280), lighter gray for dark mode (#9CA3AF). Example for an "Ocean Breeze" theme: { "light": { "colors": { "primaryBg": "#F0F8FF", "secondaryBg": "#E6F3FF", "primaryText": "#0A3D5C", "secondaryText": "#1E5A7A", "primaryAccent": "#0077BE", "visualAccent1": "#0077BE", "visualAccent2": "#2D8B8B", "visualAccent3": "#B85C38", "visualAccent4": "#8B7355", "assistantMsgBg": "rgba(0, 119, 190, 0.15)", "assistantMsgAccent": "#0077BE", "userMsgBgAlpha": "rgba(45, 139, 139, 0.18)", "userMsgAccent": "#2D8B8B", "scrollbarThumb": "#94A3B8", "scrollbarTrack": "#E2E8F0", "folderSelectedBg": "#0077BE", "folderSelectedText": "#FFFFFF", "emptyStateText": "#6B7280", "webIndicatorOnBg": "rgba(0, 119, 190, 0.15)", "webIndicatorOnBorder": "rgba(0, 119, 190, 0.3)", "webIndicatorOffBg": "rgba(0, 0, 0, 0.05)", "webIndicatorOffBorder": "rgba(0, 0, 0, 0.1)" } }, "dark": { "colors": { "primaryBg": "#0A1929", "secondaryBg": "#132F4C", "primaryText": "#E3F2FD", "secondaryText": "#B3D9FF", "primaryAccent": "#66B2FF", "visualAccent1": "#66B2FF", "visualAccent2": "#4DB8A8", "visualAccent3": "#FF8A65", "visualAccent4": "#BCAAA4", "assistantMsgBg": "rgba(102, 178, 255, 0.15)", "assistantMsgAccent": "#66B2FF", "userMsgBgAlpha": "rgba(77, 184, 168, 0.18)", "userMsgAccent": "#4DB8A8", "scrollbarThumb": "#64748B", "scrollbarTrack": "#1E293B", "folderSelectedBg": "#3B82F6", "folderSelectedText": "#FFFFFF", "emptyStateText": "#9CA3AF", "webIndicatorOnBg": "rgba(102, 178, 255, 0.2)", "webIndicatorOnBorder": "rgba(102, 178, 255, 0.4)", "webIndicatorOffBg": "rgba(255, 255, 255, 0.05)", "webIndicatorOffBorder": "rgba(255, 255, 255, 0.15)" } } }',
      inputSchema: z.object({
        name: z
          .string()
          .describe('Filename with .json extension (e.g., "my_theme.json")'),
        theme: z
          .record(z.string(), z.any())
          .describe(
            'Theme JSON object with required structure: { light: { colors: {...} }, dark: { colors: {...} } }'
          ),
        description: z
          .string()
          .optional()
          .describe('Short human-readable description'),
      }),
      execute: async ({
        name,
        theme: themeJson,
        description,
      }: {
        name: string
        theme: any
        description?: string
      }) => {
        try {
          if (!name.endsWith('.json')) {
            throw new Error('Filename must end with .json')
          }

          // Validate theme structure
          if (!themeJson || typeof themeJson !== 'object') {
            throw new Error('Theme must be a valid object')
          }

          if (!('light' in themeJson) || !('dark' in themeJson)) {
            throw new Error(
              'Theme must include both "light" and "dark" modes. Required format: { light: { colors: {...} }, dark: { colors: {...} } }'
            )
          }

          if (!themeJson.light?.colors || !themeJson.dark?.colors) {
            throw new Error(
              'Both light and dark modes must have a "colors" object'
            )
          }

          const response = await fetchWithAuth(`${API_URL}/api/themes/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name,
              content: JSON.stringify(themeJson, null, 2),
              description,
              language: 'json',
              sessionId,
            }),
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to create theme')
          }

          const data = await response.json()

          return { id: data.id, name: data.name, theme: themeJson }
        } catch (error) {
          console.error('Error in create_theme tool:', error)
          throw error
        }
      },
    },
    list_themes: {
      description:
        'List all themes created by the user. Use this to see what themes are available. Returns a list of themes with their IDs, names, descriptions, and creation dates.',
      inputSchema: z.object({}),
      execute: async () => {
        try {
          const response = await fetchWithAuth(`${API_URL}/api/themes/list`, {
            method: 'GET',
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to list themes')
          }

          const data = await response.json()

          return { themes: data.themes || [] }
        } catch (error) {
          console.error('Error in list_themes tool:', error)
          throw error
        }
      },
    },
    read_theme: {
      description:
        'Read the full contents of an existing theme. Use this when you need to see the actual theme data before updating it or to understand its current colors and structure. Returns the complete theme data including the theme JSON object.',
      inputSchema: z.object({
        id: z
          .string()
          .describe(
            'The theme ID (hex string) from the "id" field in list_themes or create_theme response.'
          ),
      }),
      execute: async ({ id }: { id: string }) => {
        try {
          const response = await fetchWithAuth(`${API_URL}/api/themes/${id}`, {
            method: 'GET',
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(
              errorData.error || `Theme not found with ID "${id}".`
            )
          }

          const data = await response.json()
          const themeJson = JSON.parse(data.theme.content)

          return {
            id: data.theme.id,
            name: data.theme.name,
            theme: themeJson,
            description: data.theme.description,
            language: data.theme.language,
            type: data.theme.type,
            updatedAt: data.theme.updatedAt,
          }
        } catch (error) {
          console.error('Error in read_theme tool:', error)
          throw error
        }
      },
    },
    update_theme: {
      description:
        'Update an existing theme. Use this when the user asks you to modify, edit, or update an existing theme. You can update the name, theme content, description, or any combination of these fields. IMPORTANT: If updating the theme object, it MUST follow the required format with both "light" and "dark" modes: { "light": { "colors": {...} }, "dark": { "colors": {...} } }. Only provide the fields you want to update.',
      inputSchema: z.object({
        id: z
          .string()
          .describe(
            'The ID of the theme to update (from previous create_theme response or from list_themes)'
          ),
        name: z
          .string()
          .optional()
          .describe('New filename with .json extension (optional)'),
        theme: z
          .record(z.string(), z.any())
          .optional()
          .describe(
            'New theme JSON object with structure: { light: { colors: {...} }, dark: { colors: {...} } } (optional)'
          ),
        description: z
          .string()
          .optional()
          .describe('New description (optional)'),
      }),
      execute: async ({
        id,
        name,
        theme: themeJson,
        description,
      }: {
        id: string
        name?: string
        theme?: any
        description?: string
      }) => {
        try {
          if (name && !name.endsWith('.json')) {
            throw new Error('Filename must end with .json')
          }

          // Validate theme structure if provided
          if (themeJson) {
            if (!themeJson || typeof themeJson !== 'object') {
              throw new Error('Theme must be a valid object')
            }

            if (!('light' in themeJson) || !('dark' in themeJson)) {
              throw new Error(
                'Theme must include both "light" and "dark" modes. Required format: { light: { colors: {...} }, dark: { colors: {...} } }'
              )
            }

            if (!themeJson.light?.colors || !themeJson.dark?.colors) {
              throw new Error(
                'Both light and dark modes must have a "colors" object'
              )
            }
          }

          const body: any = {}
          if (name) body.name = name
          if (themeJson) body.content = JSON.stringify(themeJson, null, 2)
          if (description !== undefined) body.description = description

          const response = await fetchWithAuth(
            `${API_URL}/api/themes/${id}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(body),
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to update theme')
          }

          const data = await response.json()

          return {
            id: data.id,
            name: data.name,
            theme: themeJson || JSON.parse(data.content || '{}'),
          }
        } catch (error) {
          console.error('Error in update_theme tool:', error)
          throw error
        }
      },
    },
  }
}

/**
 * Creates an identity tool definition for AI model tool calling on the frontend.
 * Identities are AI personas with custom system prompts.
 */
export function createIdentityTool(sessionId: string) {
  return {
    create_identity: {
      description:
        'Create a new AI identity with a custom system prompt. An identity is an AI persona that can have unique behaviors, expertise, personality, and communication style. Each identity gets its own conversation history. Use this when the user wants to create a specialized AI assistant, character, or persona. The identity name will be used to create a separate session.',
      inputSchema: z.object({
        name: z
          .string()
          .describe(
            'Identity name with .json extension (e.g., "researcher.json", "creative_writer.json")'
          ),
        systemPrompt: z
          .string()
          .describe(
            'The custom system prompt that defines this identity\'s behavior, personality, expertise, and communication style'
          ),
        description: z
          .string()
          .optional()
          .describe('Brief description of this identity\'s purpose and capabilities'),
        personality: z
          .string()
          .optional()
          .describe('Personality traits (e.g., "professional", "creative", "analytical")'),
        expertise: z
          .array(z.string())
          .optional()
          .describe('Areas of expertise (e.g., ["Python", "Machine Learning", "Data Science"])'),
      }),
      execute: async ({
        name,
        systemPrompt,
        description,
        personality,
        expertise,
      }: {
        name: string
        systemPrompt: string
        description?: string
        personality?: string
        expertise?: string[]
      }) => {
        try {
          if (!name.endsWith('.json')) {
            throw new Error('Identity name must end with .json')
          }

          const config = {
            systemPrompt,
            description,
            personality,
            expertise,
          }

          const response = await fetchWithAuth(`${API_URL}/api/identities/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name,
              content: JSON.stringify(config, null, 2),
              description,
              language: 'json',
              sessionId,
            }),
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to create identity')
          }

          const data = await response.json()

          return {
            id: data.id,
            name: data.name,
            url: `/identities/${data.id}`,
          }
        } catch (error) {
          console.error('Error in create_identity tool:', error)
          throw error
        }
      },
    },
    list_identities: {
      description:
        'List all AI identities created by the user. Use this to see what identities are available. Returns a list of identities with their IDs, names, descriptions, and creation dates.',
      inputSchema: z.object({}),
      execute: async () => {
        try {
          const response = await fetchWithAuth(`${API_URL}/api/identities/list`)

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to list identities')
          }

          const data = await response.json()

          return { identities: data.identities || [] }
        } catch (error) {
          console.error('Error in list_identities tool:', error)
          throw error
        }
      },
    },
    read_identity: {
      description:
        'Read the full contents of an existing AI identity. Use this when you need to see the actual identity configuration before updating it, including its system prompt, personality, and expertise. Returns the complete identity data.',
      inputSchema: z.object({
        id: z
          .string()
          .describe(
            'The identity ID (hex string) from the "id" field in list_identities or create_identity response.'
          ),
      }),
      execute: async ({ id }: { id: string }) => {
        try {
          const response = await fetchWithAuth(`${API_URL}/api/identities/${id}`)

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(
              errorData.error || `Identity not found with ID "${id}".`
            )
          }

          const data = await response.json()
          const config = JSON.parse(data.identity.content)

          return {
            id: data.identity.id,
            name: data.identity.name,
            systemPrompt: config.systemPrompt,
            description: config.description,
            personality: config.personality,
            expertise: config.expertise,
            type: data.identity.type,
            updatedAt: data.identity.updatedAt,
          }
        } catch (error) {
          console.error('Error in read_identity tool:', error)
          throw error
        }
      },
    },
    update_identity: {
      description:
        'Update an existing AI identity. Use this when the user asks you to modify, edit, or update an existing identity. You can update the name, system prompt, description, personality, expertise, or any combination of these fields. Only provide the fields you want to update.',
      inputSchema: z.object({
        id: z
          .string()
          .describe('The ID of the identity to update (from list_identities)'),
        name: z
          .string()
          .optional()
          .describe('New name with .json extension (optional)'),
        systemPrompt: z
          .string()
          .optional()
          .describe('New system prompt (optional)'),
        description: z
          .string()
          .optional()
          .describe('New description (optional)'),
        personality: z
          .string()
          .optional()
          .describe('New personality traits (optional)'),
        expertise: z
          .array(z.string())
          .optional()
          .describe('New areas of expertise (optional)'),
      }),
      execute: async ({
        id,
        name,
        systemPrompt,
        description,
        personality,
        expertise,
      }: {
        id: string
        name?: string
        systemPrompt?: string
        description?: string
        personality?: string
        expertise?: string[]
      }) => {
        try {
          if (name && !name.endsWith('.json')) {
            throw new Error('Identity name must end with .json')
          }

          // Fetch current identity
          const getResponse = await fetchWithAuth(`${API_URL}/api/identities/${id}`)

          if (!getResponse.ok) {
            const errorData = await getResponse.json()
            throw new Error(errorData.error || 'Identity not found')
          }

          const { identity } = await getResponse.json()
          const existingConfig = JSON.parse(identity.content)

          // Merge changes
          const updatedConfig = {
            systemPrompt: systemPrompt || existingConfig.systemPrompt,
            description: description !== undefined ? description : existingConfig.description,
            personality: personality !== undefined ? personality : existingConfig.personality,
            expertise: expertise !== undefined ? expertise : existingConfig.expertise,
          }

          const response = await fetchWithAuth(
            `${API_URL}/api/identities/${id}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name,
                content: JSON.stringify(updatedConfig, null, 2),
                description,
              }),
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to update identity')
          }

          const data = await response.json()

          return {
            id: data.id,
            name: data.name,
            url: `/identities/${id}`,
          }
        } catch (error) {
          console.error('Error in update_identity tool:', error)
          throw error
        }
      },
    },
  }
}

/**
 * Creates marketplace tools for searching, listing, and purchasing items
 */
export function createMarketplaceTools(userId: string) {
  return {
    search_marketplace: {
      description:
        'Search the marketplace for artifacts, themes, or identities that match user needs using semantic search. The search understands meaning and context, not just keywords, so users can search naturally (e.g., "something for productivity" will find productivity-related items even if they don\'t use that exact word). Use this when the user expresses interest in getting new content, themes, or AI personalities. Returns a list of available items with their prices, descriptions, and IDs.',
      inputSchema: z.object({
        query: z
          .string()
          .describe(
            'Natural language search query - semantic search understands meaning and context (e.g., "dark theme", "productivity tools", "creative AI", "something for coding", "helpful utilities")'
          ),
        item_type: z
          .enum(['artifact', 'theme', 'identity'])
          .optional()
          .describe('Filter by item type (optional)'),
        max_price: z
          .number()
          .optional()
          .describe('Maximum price in USD (optional)'),
        tags: z
          .array(z.string())
          .optional()
          .describe('Filter by tags (optional)'),
      }),
      execute: async ({
        query,
        item_type,
        max_price,
        tags,
      }: {
        query: string
        item_type?: 'artifact' | 'theme' | 'identity'
        max_price?: number
        tags?: string[]
      }) => {
        try {
          const params = new URLSearchParams({
            q: query,
          })

          if (item_type) params.append('item_type', item_type)
          if (max_price) params.append('max_price', max_price.toString())
          if (tags) {
            for (const tag of tags) {
              params.append('tags', tag)
            }
          }

          const response = await fetchWithAuth(
            `${API_URL}/api/marketplace/search?${params}`,
            {
              method: 'GET',
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to search marketplace')
          }

          const data = await response.json()

          // Strip embeddings to avoid large payloads
          const listings = (data.listings || []).map((item: any) => {
            const { embedding, ...rest } = item
            return rest
          })

          return {
            listings,
            total: data.total || 0,
          }
        } catch (error) {
          console.error('Error in search_marketplace tool:', error)
          throw error
        }
      },
    },
    get_top_marketplace_items: {
      description:
        'Get the top 10 most popular items in the marketplace ranked by purchase count. Use this when the user wants to see trending, popular, best-selling, or most purchased items. Returns a list of the most popular items with their purchase counts.',
      inputSchema: z.object({}),
      execute: async () => {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/api/marketplace/top-items`,
            {
              method: 'GET',
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to fetch top items')
          }

          const data = await response.json()

          // Strip embeddings to avoid large payloads
          const listings = (data.listings || []).map((item: any) => {
            const { embedding, ...rest } = item
            return rest
          })

          return {
            listings,
            total: data.total || 0,
          }
        } catch (error) {
          console.error('Error in get_top_marketplace_items tool:', error)
          throw error
        }
      },
    },
    purchase_marketplace_item: {
      description:
        'Purchase an item from the marketplace using x402 micropayments. The purchase will be paid from the user\'s wallet to the creator\'s wallet automatically. The agent has full autonomy to make purchases.',
      inputSchema: z.object({
        listing_id: z
          .string()
          .describe(
            'The ID of the marketplace listing to purchase (from search_marketplace results)'
          ),
      }),
      execute: async ({
        listing_id,
      }: {
        listing_id: string
      }) => {
        try {
          // Get the wallet - try cache first, otherwise load with passkey auth
          let wallet = getCachedWallet()
          if (!wallet) {
            try {
              wallet = await loadWallet()
            } catch (err) {
              throw new Error(
                'No wallet available. Please create a wallet first using /generate or ensure your wallet is loaded.'
              )
            }
          }

          // Get access token from Supabase session
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.access_token) {
            throw new Error('Not authenticated. Please log in first.')
          }
          const accessToken = session.access_token

          // Make the purchase using x402 protocol
          // This will automatically handle the payment flow with the wallet
          const result = await payForListing(
            listing_id,
            wallet.privateKey,
            accessToken
          )

          return {
            success: true,
            purchase_id: result.purchase_id,
            item_type: result.item_type,
            share_id: result.share_id,
            tx_hash: result.tx_hash,
            message: 'Purchase completed successfully! The item has been added to your collection.',
          }
        } catch (error) {
          console.error('Error in purchase_marketplace_item tool:', error)

          // Extract detailed error message
          let errorMessage = 'Unknown error'

          if (error && typeof error === 'object') {
            const err = error as any

            // Check for axios error response
            if (err.response?.data) {
              const data = err.response.data
              errorMessage = data.error || data.message || JSON.stringify(data)
            } else if (err.message) {
              errorMessage = err.message
            }

            // Add helpful context for common errors
            if (errorMessage.toLowerCase().includes('insufficient')) {
              errorMessage = `Insufficient funds in wallet to complete purchase. Please add funds and try again.`
            } else if (errorMessage.toLowerCase().includes('timeout')) {
              errorMessage = `Request timed out - payment provider may be unavailable. Please try again.`
            } else if (errorMessage.toLowerCase().includes('network')) {
              errorMessage = `Network error - please check your connection and try again.`
            } else if (errorMessage.toLowerCase().includes('not found')) {
              errorMessage = `Listing not found - it may have been removed or is no longer available.`
            }
          }

          throw new Error(`Purchase failed: ${errorMessage}`)
        }
      },
    },
    list_item_for_sale: {
      description:
        'List one of the user\'s artifacts, themes, or identities for sale on the marketplace. Use this when the user wants to sell their creations.',
      inputSchema: z.object({
        item_type: z
          .enum(['artifact', 'theme', 'identity'])
          .describe('The type of item to list'),
        item_id: z.string().describe('The ID of the item to list'),
        price_usd: z
          .number()
          .min(0.01)
          .max(1000)
          .describe('Price in USD (between $0.01 and $1000)'),
        title: z
          .string()
          .min(1)
          .max(200)
          .describe('A short, descriptive title for the listing'),
        description: z
          .string()
          .min(1)
          .max(2000)
          .describe('Detailed description of what the item does and why it\'s valuable'),
        tags: z
          .array(z.string())
          .max(10)
          .optional()
          .describe('Tags to help users discover this item (max 10)'),
      }),
      execute: async ({
        item_type,
        item_id,
        price_usd,
        title,
        description,
        tags = [],
      }: {
        item_type: 'artifact' | 'theme' | 'identity'
        item_id: string
        price_usd: number
        title: string
        description: string
        tags?: string[]
      }) => {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/api/marketplace/list`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                item_type,
                item_id,
                price_usd,
                title,
                description,
                tags,
              }),
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to create listing')
          }

          const data = await response.json()

          return {
            listing_id: data.listing.id,
            title: data.listing.title,
            price: data.listing.price_usd,
            x402_purchase_url: data.x402_purchase_url,
            item_type: data.listing.item_type,
            message: `Successfully listed "${title}" for $${price_usd.toFixed(2)}!`,
          }
        } catch (error) {
          console.error('Error in list_item_for_sale tool:', error)
          throw error
        }
      },
    },
    unlist_item: {
      description:
        'Remove an item from the marketplace. The item will no longer be available for purchase, but existing buyers retain access.',
      inputSchema: z.object({
        listing_id: z.string().describe('The ID of the marketplace listing to unlist'),
      }),
      execute: async ({ listing_id }: { listing_id: string }) => {
        try {
          // Use DELETE endpoint to deactivate the listing
          const response = await fetchWithAuth(
            `${API_URL}/api/marketplace/listing/${listing_id}`,
            {
              method: 'DELETE',
            }
          )

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || 'Failed to unlist item')
          }

          const data = await response.json()

          return {
            success: data.success,
            listing_id,
            item_type: data.item_type,
            message: `Successfully unlisted the item. It's no longer available for purchase.`,
          }
        } catch (error) {
          console.error('Error in unlist_item tool:', error)
          const errorMessage = error instanceof Error ? error.message : 'Failed to unlist item'
          throw new Error(`Unlist failed: ${errorMessage}`)
        }
      },
    },
  }
}

